import React, { useRef, useState } from 'react'
import BaseTable, {
  ColumnDefinition
} from 'presentation/shared/components/BaseTable'
import { ReactComponent as EditIcon } from 'presentation/assets/icons/edit-rounded.svg'
import { ReactComponent as TrashIcon } from 'presentation/assets/icons/delete-rounded.svg'
import { ProcedureV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { DataSource } from 'common/enum/data-source'
import {
  QuantityWrapper,
  QuantityInput,
  EditIconButton
} from '../components/styles'
import {
  DeleteIconButton,
  StyledCheckbox,
  MainBadge
} from '../../components/styles'
import capitalizeFirstLetter from 'common/utils/capitalizeFirstLetter'

type Props = {
  initialRows: ProcedureV2Props[]
  onDelete: (procedureCode: string) => void
  onQuantityChange?: (procedureCode: string, quantity: number) => void
  onToggleSelection?: (procedureCode: string) => void
  hideSections?: boolean
  formatDescription?: boolean
}

function ProceduresTable({
  initialRows,
  onDelete,
  onQuantityChange,
  onToggleSelection,
  hideSections = false,
  formatDescription = false
}: Props) {
  const hospitalRows = initialRows.filter(
    (row) => row.source === DataSource.TEMPLATE
  )
  const doctorRows = initialRows.filter(
    (row) => row.source === DataSource.DOCTOR
  )

  const isFromHospitalTemplate = (procedureCode: string) => {
    return hospitalRows.some((row) => row.code === procedureCode)
  }

  const sections = hideSections
    ? [{ title: '', rows: initialRows }]
    : [
        {
          title: 'Gabarito',
          rows: hospitalRows
        },
        {
          title: 'Inclusões do médico',
          rows: doctorRows
        }
      ]
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})
  const [editingValues, setEditingValues] = useState<{ [key: string]: string }>(
    {}
  )

  const handleEditClick = (code: string) => {
    inputRefs.current[code]?.focus()
    inputRefs.current[code]?.select()
  }

  const handleQuantityChange = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    setEditingValues((prev) => ({ ...prev, [code]: numericValue }))

    if (numericValue !== '') {
      const quantity = parseInt(numericValue, 10)
      if (!isNaN(quantity) && quantity > 0 && quantity <= 99) {
        onQuantityChange?.(code, quantity)
      }
    }
  }

  const handleQuantityBlur = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    const quantity = parseInt(numericValue, 10)

    if (isNaN(quantity) || quantity <= 0 || numericValue === '') {
      onQuantityChange?.(code, 1)
    }

    setEditingValues((prev) => {
      const newValues = { ...prev }
      delete newValues[code]
      return newValues
    })
  }

  const handleQuantityFocus = (code: string, currentQuantity: number) => {
    const formattedValue =
      currentQuantity < 10 ? `0${currentQuantity}` : currentQuantity.toString()
    setEditingValues((prev) => ({ ...prev, [code]: formattedValue }))
  }

  const columns: ColumnDefinition<ProcedureV2Props>[] = [
    {
      key: 'badge',
      header: '',
      width: '5%',
      centered: true,
      render: function renderBadge(row: ProcedureV2Props) {
        if (row.is_main) {
          return <MainBadge>Principal</MainBadge>
        }

        const isCheckboxDisabled = !isFromHospitalTemplate(row.code)

        return (
          <StyledCheckbox
            checked={row.is_selected || false}
            onChange={() => onToggleSelection?.(row.code)}
            disabled={isCheckboxDisabled}
          />
        )
      }
    },
    {
      key: 'code',
      header: 'Código',
      width: '10%',
      centered: true,
      render: (row) => row.code
    },
    {
      key: 'name',
      header: 'Procedimento',
      width: '70%',
      render: (row) =>
        formatDescription
          ? capitalizeFirstLetter(row.description)
          : row.description
    },
    {
      key: 'quantity',
      header: 'Quant.',
      width: '5%',
      centered: true,
      render: function renderQuantity(row) {
        const isEditing = row.code in editingValues
        const displayValue = isEditing
          ? editingValues[row.code]
          : row.quantity < 10
          ? `0${row.quantity}`
          : row.quantity.toString()

        return (
          <QuantityWrapper>
            <QuantityInput
              ref={(el) => (inputRefs.current[row.code] = el)}
              type="text"
              value={displayValue}
              maxLength={2}
              onChange={(e) => handleQuantityChange(row.code, e.target.value)}
              onBlur={(e) => handleQuantityBlur(row.code, e.target.value)}
              onFocus={() => handleQuantityFocus(row.code, row.quantity)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
            />
            <EditIconButton
              size="small"
              onClick={() => handleEditClick(row.code)}
            >
              <EditIcon width={20} height={20} />
            </EditIconButton>
          </QuantityWrapper>
        )
      }
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '10%',
      centered: true,
      render: function renderActions(row) {
        const isDisabled = row.is_main || isFromHospitalTemplate(row.code)

        return (
          <DeleteIconButton
            size="small"
            onClick={() => onDelete(row.code)}
            disabled={isDisabled}
          >
            <TrashIcon width={20} height={20} />
          </DeleteIconButton>
        )
      }
    }
  ]

  return (
    <BaseTable
      sections={sections}
      columns={columns}
      emptyState="Nenhum procedimento foi adicionado ainda"
    />
  )
}

export default ProceduresTable
